import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  ICreateUbicacion,
  IGeoJSONCircle,
  IGeoJSONPoint,
  IUbicacionBase,
  IUpdateUbicacion,
  IValoresUbicacionCentroAtencion,
} from 'modelos/src';
import { AuxiliaresModule } from '../../../../../../auxiliares/auxiliares.module';
import { CircleEditorMapboxComponent } from '../../../../../../auxiliares/circle-editor-mapbox/circle-editor-mapbox.component';
import { HelperService } from '../../../../../../auxiliares/servicios/helper.service';
import { UbicacionService } from '../../../../../../auxiliares/servicios/http/ubicacion.service';
import { LoginService } from '../../../../../login/login.service';

type ICentro = IUbicacionBase<'Centro de Atención'>;

/**
 * Base de operaciones (IUbicacion 'Centro de Atención'): punto con radio de
 * cobertura. Sólo médica: `valores.tipoEmergencia` siempre es 'Médica'.
 */
@Component({
  selector: 'app-crear-editar-centro-atencion',
  imports: [AuxiliaresModule, CircleEditorMapboxComponent],
  templateUrl: './crear-editar-centro-atencion.component.html',
  styleUrl: '../../../form-dialogo.scss',
})
export class CrearEditarCentroAtencionComponent implements OnInit {
  helper = inject(HelperService);
  private service = inject(UbicacionService);
  // Siempre en diálogo: { id } edita; sin datos, crea.
  data = inject<{ id?: string } | null>(MAT_DIALOG_DATA, { optional: true });
  dialogRef =
    inject<MatDialogRef<CrearEditarCentroAtencionComponent>>(MatDialogRef);

  public id?: string | null;
  public loading = true;
  public guardando = false;
  public form?: FormGroup;
  public title = '';
  public subtitle?: string;
  public centro?: ICentro;
  public mostrarCliente = HelperService.getTipoCliente() !== 'Final';
  public colorTema = LoginService.getCliente()?.config?.tema?.primaryColor;

  public circulo?: IGeoJSONCircle;
  public centrarA?: IGeoJSONPoint;

  private createForm(): void {
    const c = this.centro;
    this.form = new FormGroup({
      identificacion: new FormControl(c?.identificacion, Validators.required),
      idCliente: new FormControl(
        c?.idCliente ?? LoginService.getCliente()?._id,
        Validators.required,
      ),
      activo: new FormControl(this.id ? !!c?.valores?.activo : true),
      telefono: new FormControl(c?.valores?.telefono),
      email: new FormControl(c?.valores?.email, Validators.email),
      direccion: new FormControl(c?.direccion, Validators.required),
      geojson: new FormControl(c?.geojson, Validators.required),
    });
  }

  public get radio(): string | null {
    const r = this.circulo?.radius;
    return r ? `${Math.round(r).toLocaleString('es-AR')} m` : null;
  }

  private getData(): ICreateUbicacion | IUpdateUbicacion {
    const v = this.form!.value;
    const valores: IValoresUbicacionCentroAtencion = {
      telefono: v.telefono || undefined,
      email: v.email || undefined,
      tipoEmergencia: 'Médica',
      activo: !!v.activo,
    };
    const data: ICreateUbicacion = {
      categoria: 'Centro de Atención',
      identificacion: v.identificacion?.trim(),
      direccion: v.direccion,
      geojson: v.geojson,
      valores,
    };
    if (!this.id && v.idCliente) data.idCliente = v.idCliente;
    return data;
  }

  public async onSubmit(): Promise<void> {
    if (!this.form?.valid) return;
    this.guardando = true;
    try {
      if (this.id) {
        await this.service.update(this.id, this.getData() as IUpdateUbicacion);
        const actualizado = (await this.service.getById(this.id)) as ICentro;
        this.helper.notifSuccess(
          'Base de operaciones actualizada correctamente',
        );
        this.close(actualizado);
      } else {
        const creado = (await this.service.create(
          this.getData() as ICreateUbicacion,
        )) as ICentro;
        this.helper.notifSuccess('Base de operaciones creada correctamente');
        this.close(creado);
      }
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    } finally {
      this.guardando = false;
    }
  }

  public close(centro?: ICentro): void {
    this.dialogRef.close(centro);
  }

  public patchDireccion(direccion: string): void {
    this.form?.patchValue({ direccion });
  }

  public onCircleChange(
    circulo: { center?: [number, number]; radius?: number } | undefined,
  ): void {
    if (!circulo?.center || typeof circulo.radius !== 'number') return;
    const geojson: IGeoJSONCircle = {
      type: 'Point',
      coordinates: circulo.center,
      radius: circulo.radius,
    };
    this.circulo = geojson;
    this.centrarA = { type: 'Point', coordinates: circulo.center };
    this.form?.patchValue({ geojson });
  }

  async ngOnInit(): Promise<void> {
    try {
      this.id = this.data?.id;
      if (this.id) {
        this.centro = (await this.service.getById(this.id)) as ICentro;
        const geo = this.centro?.geojson as IGeoJSONCircle | undefined;
        if (geo?.coordinates) {
          this.circulo = geo;
          this.centrarA = { type: 'Point', coordinates: geo.coordinates };
        }
      }
      this.title = this.id
        ? 'Editar base de operaciones'
        : 'Crear base de operaciones';
      this.subtitle = this.centro?.identificacion;
      this.createForm();
    } catch (error) {
      console.error(error);
      this.helper.notifError(error);
    } finally {
      this.loading = false;
    }
  }
}
